export enum ZIndex {
  base = 1,
  seat,
  table,
  chip,
  card,
  player,
  zoom,
  action,
  header,
  widget,
  overlay
}

/**
 * EN-10520 — the table's three layering BANDS, in one place.
 *
 * The old numbers were scattered literals and they disagreed: an opponent seat
 * wrapper sat at 3 while the floating dealer button sat at 5, and inside a seat
 * the dealer chip's own `zIndex: 5` outranked the hole-card layer's 4. Both are
 * why the dealer button covered revealed cards at showdown.
 *
 * Read top to bottom:
 *
 *  1. **Dock / action prompt** — `footerRow` in pages/gamePageLayout.ts owns
 *     this band (zIndex 10 at PAGE level, a separate stacking context from the
 *     felt). It is deliberately NOT re-declared here: the dock must keep
 *     painting above everything, including the win animation, and this ticket
 *     does not touch it.
 *  2. **Seat elements** — cards, avatar, username, balance, action badge.
 *  3. **Table elements** — bet chips, board cards, dealer button, blind markers.
 *
 * `table*` values are what a table element may use; `seat*` values sit above
 * every one of them. `within*` values order children INSIDE a seat's own
 * stacking context (the seat wrapper's z-index opens one), so they only need to
 * be consistent with each other.
 */
export const TableZ = {
  /** Felt furniture: bet chips, returned-bet chips, board cards, dealer button. */
  tableElement: 5,
  /**
   * EN-11257: the Sit & Go waiting panel in the felt's centre. On the
   * table-element band, so every seat card paints over it where they meet; it
   * is only shown before the first hand, when the felt has no chips, board or
   * dealer button for it to cover.
   */
  sitGoWaitingPanel: 5,
  /** Any seat's block — always above every table element. */
  seat: 6,
  /** The POV seat — same band, but wins ties against opponents' seats. */
  seatPov: 7,
  /**
   * The transient "Uncalled bet" banner.
   *
   * ABOVE the seats: it was on the table-element band, which is below every
   * seat, so it painted behind the player's own hole cards and could not be
   * read at all (reported 2026-09-04).
   *
   * BELOW the winner banner: both can be on screen as a hand ends, and the
   * winner is the headline.
   */
  returnedBetMessage: 8,
  /** Felt-wide winner banner — above seats, still below the page dock. */
  winnerBanner: 9,

  /** Within a seat: the dealer marker tucks UNDER the seat's own cards. */
  withinSeatDealer: 1,
  /** Within a seat: avatar / name / balance block. */
  withinSeatPlayer: 3,
  /** Within a seat: hole cards — above the avatar and the dealer marker. */
  withinSeatCards: 4,
  /** Within a seat: action badge / bounty tab. */
  withinSeatBadge: 5
} as const;
